// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, XCircle, Lock, ShieldCheck } from 'lucide-react'
import { useAuth, authErrorKey } from '@/context/auth-context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

// Google "G" SVG icon — inlined so no external asset fetch is needed.
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"
      />
    </svg>
  )
}

export default function LoginPage() {
  const { t } = useTranslation()
  const { user, loading, mfaResolver, authError, clearAuthError, signInWithGoogle, completeTotpMfa, cancelMfa } =
    useAuth()

  const [pending, setPending] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)

  // MFA step state
  const [mfaCode, setMfaCode] = useState('')
  const [mfaCodeError, setMfaCodeError] = useState<string | null>(null)
  const [mfaPending, setMfaPending] = useState(false)

  // Already authenticated → redirect away.
  if (!loading && user && !mfaResolver) {
    return <Navigate to="/orgs" replace />
  }

  async function handleSignInWithGoogle() {
    setPending(true)
    setErrorKey(null)
    clearAuthError()
    try {
      // Navigates away on success; only a synchronous config error rejects here.
      // MFA + auth errors surface on return via authError (getRedirectResult).
      await signInWithGoogle()
    } catch (err) {
      setErrorKey(authErrorKey(err))
      setPending(false)
    }
  }

  async function handleVerifyMfa() {
    // Validate: must be exactly 6 digits
    if (!/^\d{6}$/.test(mfaCode.trim())) {
      setMfaCodeError('login.invalidMfaCodeFormat')
      return
    }
    setMfaCodeError(null)
    setMfaPending(true)
    setErrorKey(null)
    try {
      await completeTotpMfa(mfaCode.trim())
    } catch (err) {
      setErrorKey(authErrorKey(err))
    } finally {
      setMfaPending(false)
    }
  }

  function handleCancelMfa() {
    setMfaCode('')
    setMfaCodeError(null)
    setErrorKey(null)
    clearAuthError()
    cancelMfa()
  }

  // A local (MFA-step) error takes precedence; otherwise show an error surfaced on
  // return from a failed federated redirect.
  const shownErrorKey = errorKey ?? authError

  return (
    <div className="min-h-svh flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[55%] relative flex-col justify-between p-12 overflow-hidden bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9]">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#02C7C3]/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-[380px] h-[380px] rounded-full bg-[#006EF9]/40 blur-3xl pointer-events-none" />

        {/* Logo area */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <span className="text-white font-bold text-lg">Query Health</span>
        </div>

        {/* Headline */}
        <div className="relative z-10 flex flex-col gap-6">
          <div>
            <p className="text-[#02C7C3] text-sm font-semibold uppercase tracking-widest mb-4">
              {t('login.operatorPortal')}
            </p>
            <h1 className="text-5xl font-bold text-white leading-tight mb-5">
              {t('login.headline')}
            </h1>
            <p className="text-white/65 text-lg leading-relaxed max-w-sm">
              {t('login.subheadline')}
            </p>
          </div>

          {/* Operator feature pills */}
          <div className="flex flex-col gap-3 max-w-sm">
            {([
              t('login.featureOrgs'),
              t('login.featureUsage'),
              t('login.featureSettings'),
            ] as string[]).map((label) => (
              <div
                key={label}
                className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/15 px-4 py-3"
              >
                <div className="h-2 w-2 rounded-full bg-[#02C7C3] shrink-0" />
                <span className="text-white/80 text-sm">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex items-center justify-center px-6 bg-secondary relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[320px] h-[320px] rounded-full bg-[#02C7C3]/6 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-16 w-[280px] h-[280px] rounded-full bg-[#006EF9]/8 blur-3xl pointer-events-none" />

        <div className="relative rounded-3xl border border-border bg-card overflow-hidden w-full max-w-md z-10 shadow-lg">
          <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9]" />

          <div className="p-6 sm:p-10">
            {/* Header */}
            <div className="flex flex-col items-center mb-8">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#006EF9]/15 to-[#02C7C3]/15 border border-border flex items-center justify-center mb-4">
                <ShieldCheck className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">
                {mfaResolver ? t('login.mfaTitle') : t('login.welcomeBack')}
              </h2>
              <p className="text-sm text-muted-foreground mt-1 text-center">
                {mfaResolver ? t('login.mfaPrompt') : t('login.signInSubtitle')}
              </p>
            </div>

            {/* Error banner */}
            {shownErrorKey && (
              <div className="mb-5 flex items-center gap-3 px-4 py-3 rounded-xl bg-destructive/8 border border-destructive/20">
                <XCircle className="h-4 w-4 shrink-0 text-destructive" />
                <span className="text-sm text-destructive">{t(shownErrorKey)}</span>
              </div>
            )}

            {/* MFA step */}
            {mfaResolver ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="mfa-code"
                    className="text-sm font-medium text-foreground"
                  >
                    {t('login.mfaCodeLabel')}
                  </label>
                  <Input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={mfaCode}
                    onChange={e => {
                      setMfaCode(e.target.value)
                      setMfaCodeError(null)
                    }}
                    aria-label={t('login.mfaCodeLabel')}
                  />
                  {mfaCodeError && (
                    <p className="text-xs text-destructive mt-0.5">{t(mfaCodeError)}</p>
                  )}
                </div>

                <Button
                  size="full"
                  onClick={() => void handleVerifyMfa()}
                  disabled={mfaPending}
                  className="mt-1"
                >
                  {mfaPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('login.signingIn')}
                    </>
                  ) : (
                    t('login.verify')
                  )}
                </Button>

                <Button
                  variant="ghost"
                  size="default"
                  onClick={handleCancelMfa}
                  className="w-full"
                >
                  {t('login.cancel')}
                </Button>
              </div>
            ) : (
              /* Google SSO step */
              <div className="flex flex-col gap-4">
                <button
                  type="button"
                  onClick={() => void handleSignInWithGoogle()}
                  disabled={pending}
                  className="w-full h-11 rounded-xl font-semibold text-sm text-foreground flex items-center justify-center gap-3 border border-border bg-card hover:bg-secondary transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                >
                  {pending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      {t('login.signingIn')}
                    </>
                  ) : (
                    <>
                      <GoogleIcon />
                      {t('login.signInWithGoogle')}
                    </>
                  )}
                </button>
              </div>
            )}

            {/* HIPAA note */}
            <div className="mt-7 pt-5 border-t border-border flex items-center justify-center gap-2">
              <Lock className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{t('login.hipaaNote')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
